import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface ScreenEdgeBorderProps {
  /** How far inward the glow fades (px) */
  edgeFade?: number;
  animationDuration?: number;
}

/**
 * Rotating gradient border that runs around all four screen edges.
 * Uses the same yellow-to-purple gradient animation as AnimatedWishlistButton.
 * Four edge-fade gradient masks create a soft feathered inner edge.
 */
export const ScreenEdgeBorder = ({ edgeFade = 40, animationDuration = 1000 }: ScreenEdgeBorderProps) => {
  const [currentFrame, setCurrentFrame] = useState(0);

  const easeInOutCubic = (x: number) =>
    x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

  const gradientFrames = Array.from({ length: 32 }, (_, i) => {
    const t = i / 31;
    const angle = 270 - easeInOutCubic(t) * 360;
    return `${(angle < 0 ? angle + 360 : angle).toFixed(2)}deg`;
  });

  useEffect(() => {
    const interval = setInterval(
      () => setCurrentFrame((prev) => (prev + 1) % 32),
      animationDuration / 32,
    );
    return () => clearInterval(interval);
  }, [animationDuration]);

  const maskValue = [
    `linear-gradient(to right, black, transparent ${edgeFade}px)`,
    `linear-gradient(to left, black, transparent ${edgeFade}px)`,
    `linear-gradient(to bottom, black, transparent ${edgeFade}px)`,
    `linear-gradient(to top, black, transparent ${edgeFade}px)`,
  ].join(', ');

  return (
    <motion.div
      className='pointer-events-none fixed inset-0'
      style={{
        zIndex: 24,
        backgroundImage: `linear-gradient(${gradientFrames[currentFrame]}, rgb(251, 191, 36) 4%, rgb(107, 33, 168) 100%)`,
        WebkitMask: maskValue,
        mask: maskValue,
      } as React.CSSProperties}
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.5 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.0, ease: 'easeInOut' }}
    />
  );
};
